import { AcademicDegreePostfixEnum, AcademicDegreePrefixEnum, CivilStatusEnum, CommunicationLockEnum, CommunicationRoleEnum, CommunicationTypeEnum, CountryCallingCodeEnum, CountryIsoCodeEnum, DiplomaticStatusEnum, EducationEnum, EmploymentEnum, FixedPartyEnum, GenderEnum, InsolvencyEnum, LanguageIsoCodeEnum, LegalFormEnum, OccupationEnum, OrganizationTypeEnum, PartySubTypeEnum, PartyTypeEnum, SalutationEnum, } from '@zurich/api/party';
import { enumWithNumberValuesToArrayOfKeys, enumWithStringValuesToArrayOfKeys, } from '@zurich/common/types';
export var PartySalutation;
(function (PartySalutation) {
    function getAllValues() {
        // "AN" is currently not supported, that's why we filter it
        return enumWithStringValuesToArrayOfKeys(SalutationEnum).filter((salutation) => salutation !== SalutationEnum.AN);
    }
    PartySalutation.getAllValues = getAllValues;
    function createOptions(t) {
        return PartySalutation.getAllValues().map((salutation) => ({
            label: PartySalutation.getTranslatedValue(salutation, t),
            value: salutation,
        }));
    }
    PartySalutation.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:salutation.${value}`);
    }
    PartySalutation.getTranslatedValue = getTranslatedValue;
    // FIXME: we need to adress this, for now we fallback to more for other gender types
    // as we only have male and female translted right now
    function inferGender(value) {
        return value === SalutationEnum.FRAU ? GenderEnum.FEMALE : GenderEnum.MALE;
    }
    PartySalutation.inferGender = inferGender;
})(PartySalutation || (PartySalutation = {}));
export var PartyAcademicDegreePostfix;
(function (PartyAcademicDegreePostfix) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(AcademicDegreePostfixEnum);
    }
    PartyAcademicDegreePostfix.getAllValues = getAllValues;
    function createOptions(salutation, t) {
        return PartyAcademicDegreePostfix.getAllValues().map((academic) => ({
            label: PartyAcademicDegreePostfix.getTranslatedValue(academic, salutation, t),
            value: PartyAcademicDegreePostfix.getTranslatedValue(academic, salutation, t),
        }));
    }
    PartyAcademicDegreePostfix.createOptions = createOptions;
    function getTranslatedValue(value, salutation, t) {
        // FIXME: we need to adress this, for now we fallback to more for other gender types
        // as we only have male and female translted right now
        return t(`party:academicDegreePostfix.${value}.${PartySalutation.inferGender(salutation)}`);
    }
    PartyAcademicDegreePostfix.getTranslatedValue = getTranslatedValue;
})(PartyAcademicDegreePostfix || (PartyAcademicDegreePostfix = {}));
export var PartyAcademicDegreePrefix;
(function (PartyAcademicDegreePrefix) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(AcademicDegreePrefixEnum);
    }
    PartyAcademicDegreePrefix.getAllValues = getAllValues;
    function createOptions(salutation, t) {
        return PartyAcademicDegreePrefix?.getAllValues().map((academic) => ({
            label: PartyAcademicDegreePrefix.getTranslatedValue(academic, salutation, t),
            value: PartyAcademicDegreePrefix.getTranslatedValue(academic, salutation, t),
        }));
    }
    PartyAcademicDegreePrefix.createOptions = createOptions;
    function getTranslatedValue(value, salutation, t) {
        return t(`party:academicDegreePrefix.${value}.${PartySalutation.inferGender(salutation)}`);
    }
    PartyAcademicDegreePrefix.getTranslatedValue = getTranslatedValue;
})(PartyAcademicDegreePrefix || (PartyAcademicDegreePrefix = {}));
export var Party;
(function (Party) {
    function isLegalEntity(party) {
        return party.partyType === 'J';
    }
    Party.isLegalEntity = isLegalEntity;
    function isPerson(party) {
        return party.partyType === 'N';
    }
    Party.isPerson = isPerson;
    function getLegalEntity(party) {
        // TODO: what should we do if type/subType is null?
        if (!isLegalEntity(party))
            throw new Error('PartyType is not LegalEntity');
        return party;
    }
    Party.getLegalEntity = getLegalEntity;
    function getPerson(party) {
        // TODO: what should we do if type/subType is null?
        if (!isPerson(party))
            throw new Error('PartyType is not Person');
        return party;
    }
    Party.getPerson = getPerson;
    function types(party) {
        return {
            isLegalEntity: isLegalEntity(party),
            isPerson: isPerson(party),
        };
    }
    Party.types = types;
    function isPrivatPerson(party) {
        return (isPerson(party) && party.partySubType === PartySubTypeEnum.PRIVATPERSON);
    }
    Party.isPrivatPerson = isPrivatPerson;
    function isEinzelunternehmer(party) {
        return (isPerson(party) &&
            party.partySubType === PartySubTypeEnum.EINZELUNTERNEHMER);
    }
    Party.isEinzelunternehmer = isEinzelunternehmer;
    function isVerein(party) {
        return (isLegalEntity(party) && party.partySubType === PartySubTypeEnum.VEREIN);
    }
    Party.isVerein = isVerein;
    function isUnternehmen(party) {
        return (isLegalEntity(party) &&
            party.partySubType === PartySubTypeEnum.UNTERNEHMEN);
    }
    Party.isUnternehmen = isUnternehmen;
    function subTypes(party) {
        return {
            isPrivatperson: isPrivatPerson(party),
            isEinzelunternehmer: isEinzelunternehmer(party),
            isVerein: isVerein(party),
            isUnternehmen: isUnternehmen(party),
        };
    }
    Party.subTypes = subTypes;
    function getName(party, t) {
        if (isPerson(party)) {
            const _party = getPerson(party);
            return [
                _party.salutation &&
                    PartySalutation.getTranslatedValue(_party.salutation, // FIXME: replace with proper handling of string union
                    t),
                // field disabled for now
                // _party.degreeAwarded,
                _party.academicDegreePrefix,
                _party.firstName,
                _party.lastName,
                _party.nameAffix,
                _party.academicDegreePostfix,
            ]
                .filter((v) => !!v)
                .join(' ');
        }
        if (isLegalEntity(party)) {
            return getLegalEntity(party).organizationName;
        }
    }
    Party.getName = getName;
    function getNameShort(party) {
        if (isPerson(party)) {
            const _party = getPerson(party);
            return [_party.firstName, _party.lastName, _party.nameAffix]
                .filter((v) => !!v)
                .join(' ');
        }
        if (isLegalEntity(party)) {
            return getLegalEntity(party).organizationName;
        }
    }
    Party.getNameShort = getNameShort;
    function getInitials(party) {
        if (Party.isPerson(party)) {
            const person = Party.getPerson(party);
            return `${person?.firstName?.charAt(0)}${person?.lastName?.charAt(0)}`;
        }
        if (Party.isLegalEntity(party)) {
            return Party.getLegalEntity(party).organizationName?.charAt(0);
        }
        return '?';
    }
    Party.getInitials = getInitials;
    // FIXME: this could be solved by backend allowing for array of strings!
    function mapForExternal(party, t) {
        //TODO: should we even expose this function?
        // convert degree id into translation based on salutation
        function resolveDegrees(degrees, options) {
            if (degrees === undefined || degrees === null)
                return null;
            const degreeParts = degrees.split(';');
            const resolved = degreeParts.map((degree) => options.find((o) => o.value === degree)?.label || degree);
            return resolved.join(' ');
        }
        // we need to remove phone number channel and geo address channel if it is incomplete
        // e.g. user didn't enter areaCode or localPhoneNumber or didn't select a country for geo address
        const extendedPartyData = {
            ...party.extendedPartyData,
            communicationChannels: party.extendedPartyData?.communicationChannels?.filter((c) => !(c.communicationChannelType === CommunicationTypeEnum.TELEPHONE &&
                !c.areaCode &&
                !c.localPhoneNumber) &&
                !(c.communicationChannelType === CommunicationTypeEnum.GEO &&
                    !c.country) &&
                !(c.communicationChannelType === CommunicationTypeEnum.EMAIL &&
                    !c.emailAddress)),
        };
        if (Party.isPerson(party)) {
            // We can not send the properties riskClass and profession with the payload because they are derived from party component based occupation value
            const { profession, riskClass, ...person } = Party.getPerson(party);
            return {
                ...person,
                // convert titles to full string based on mapping
                academicDegreePrefix: resolveDegrees(person.academicDegreePrefix, PartyAcademicDegreePrefix?.createOptions(party.salutation, t)) ?? undefined,
                academicDegreePostfix: resolveDegrees(person.academicDegreePostfix, PartyAcademicDegreePostfix?.createOptions(party.salutation, t)) ?? undefined,
                // field disabled for now
                /*degreeAwarded: resolveDegrees(
                  person.degreeAwarded,
                  PartyDegreeAwarded.createOptions(party.salutation, t)
                ),*/
                extendedPartyData,
            };
        }
        return {
            ...party,
            extendedPartyData,
        };
    }
    Party.mapForExternal = mapForExternal;
})(Party || (Party = {}));
export var PartyCommunicationChannel;
(function (PartyCommunicationChannel) {
    function createDefault(communicationChannelType) {
        if (communicationChannelType === 'EMAIL') {
            return {
                country: null,
                emailAddress: null,
                communicationRole: 'EH',
                communicationChannelType: 'EMAIL',
            };
        }
        if (communicationChannelType === 'GEO') {
            return {
                country: null,
                postalCode: null,
                city: null,
                street: null,
                houseNumber: null,
                staircase: null,
                doorNumber: null,
                deliveryNotice: null,
                communicationRole: null,
                communicationChannelType: 'GEO',
            };
        }
        if (communicationChannelType === 'POSTBOX') {
            return {
                country: null,
                postalCode: null,
                city: null,
                poboxNumber: null,
                communicationRole: null,
                communicationChannelType: 'POSTBOX',
            };
        }
        if (communicationChannelType === 'TELEPHONE') {
            return {
                countryCallingCode: '+43',
                communicationRole: 'TH',
                communicationChannelType: 'TELEPHONE',
            };
        }
        throw new Error(`CommunicationChannelType "${communicationChannelType}" not supported!`);
    }
    PartyCommunicationChannel.createDefault = createDefault;
    // Add logic we need to use when creating/updating communication channels
    function mapForExternal(communicationChannels, updatedChannel) {
        // if there is already a main channel change the existing one
        // HWS -> WA not implemented yet because of household
        const updatedChannels = communicationChannels.channels
            // filter out existing channel first (update)
            .filter((channel) => channel.commNo !== updatedChannel.commNo)
            .map((channel) => {
            // EH -> EW
            if (updatedChannel.communicationRole === CommunicationRoleEnum.EH &&
                channel.communicationRole === CommunicationRoleEnum.EH) {
                return { ...channel, communicationRole: CommunicationRoleEnum.EW };
            }
            // TH -> TW
            if (updatedChannel.communicationRole === CommunicationRoleEnum.TH &&
                channel.communicationRole === CommunicationRoleEnum.TH) {
                return { ...channel, communicationRole: CommunicationRoleEnum.TW };
            }
            return channel;
        });
        return [...updatedChannels, updatedChannel];
    }
    PartyCommunicationChannel.mapForExternal = mapForExternal;
})(PartyCommunicationChannel || (PartyCommunicationChannel = {}));
export var PartySubType;
(function (PartySubType) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(PartySubTypeEnum);
    }
    PartySubType.getAllValues = getAllValues;
    function createOptions(t) {
        return PartySubType.getAllValues().map((subType) => ({
            label: PartySubType.getTranslatedValue(subType, t),
            value: subType,
        }));
    }
    PartySubType.createOptions = createOptions;
    function getPartyType(value) {
        if (['E', 'P'].includes(value)) {
            return 'N';
        }
        return 'J';
    }
    PartySubType.getPartyType = getPartyType;
    function getTranslatedValue(value, t) {
        return t(`party:subType.${value}`);
    }
    PartySubType.getTranslatedValue = getTranslatedValue;
})(PartySubType || (PartySubType = {}));
export var PartyGender;
(function (PartyGender) {
    function getTranslatedValue(value, t) {
        return t(`party:gender.${value}`);
    }
    PartyGender.getTranslatedValue = getTranslatedValue;
})(PartyGender || (PartyGender = {}));
export var PartyType;
(function (PartyType) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(PartyTypeEnum);
    }
    PartyType.getAllValues = getAllValues;
    function getTranslatedValue(value, t) {
        return t(`party:partyType.${value}`);
    }
    PartyType.getTranslatedValue = getTranslatedValue;
})(PartyType || (PartyType = {}));
// field disabled for now
/*export namespace PartyDegreeAwarded {
  export function getAllValues() {
    return enumWithStringValuesToArrayOfKeys(DegreeAwardedEnum);
  }

  export function createOptions(
    salutation: SalutationEnum | null | undefined,
    t: TFunction
  ): UiOption<DegreeAwardedEnum>[] {
    return PartyDegreeAwarded.getAllValues().map(academic => ({
      label: PartyDegreeAwarded.getTranslatedValue(academic, salutation, t),
      value: academic,
    }));
  }

  export function getTranslatedValue(
    value: DegreeAwardedEnum,
    salutation: SalutationEnum | null | undefined,
    t: TFunction
  ) {
    return t(
      `party:degreeAwarded.${value}.${PartySalutation.inferGender(salutation)}`
    );
  }
}*/
export var PartyCommunicationLock;
(function (PartyCommunicationLock) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(CommunicationLockEnum);
    }
    PartyCommunicationLock.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyCommunicationLock.getAllValues().map((communicationLock) => ({
            label: PartyCommunicationLock.getTranslatedValue(communicationLock, t),
            value: communicationLock,
        }));
    }
    PartyCommunicationLock.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:communicationLock.${value}`);
    }
    PartyCommunicationLock.getTranslatedValue = getTranslatedValue;
})(PartyCommunicationLock || (PartyCommunicationLock = {}));
export var PartyCommunicationRole;
(function (PartyCommunicationRole) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(CommunicationRoleEnum);
    }
    PartyCommunicationRole.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyCommunicationRole.getAllValues()
            .filter((communicationRole) => communicationRole !== CommunicationRoleEnum.WA &&
            communicationRole !== CommunicationRoleEnum.KA)
            .map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createOptions = createOptions;
    function createPhoneOptions(t) {
        return [CommunicationRoleEnum.TH, CommunicationRoleEnum.TW].map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createPhoneOptions = createPhoneOptions;
    function createEmailOptions(t) {
        return [CommunicationRoleEnum.EH, CommunicationRoleEnum.EW].map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createEmailOptions = createEmailOptions;
    function createPostboxOptions(t) {
        return [CommunicationRoleEnum.KA, CommunicationRoleEnum.WA].map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createPostboxOptions = createPostboxOptions;
    function createPersonOptions(t) {
        return [
            CommunicationRoleEnum.HWS,
            CommunicationRoleEnum.NWS,
            CommunicationRoleEnum.ZUS,
            CommunicationRoleEnum.KA,
            // CommunicationRoleEnum.WA,
        ].map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createPersonOptions = createPersonOptions;
    function createEinzelunternehmerOptions(t) {
        return [
            CommunicationRoleEnum.HWS,
            CommunicationRoleEnum.NWS,
            CommunicationRoleEnum.FIRM,
            // CommunicationRoleEnum.KA,
            CommunicationRoleEnum.BS,
            // CommunicationRoleEnum.WA,
        ].map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createEinzelunternehmerOptions = createEinzelunternehmerOptions;
    function createCompanyOptions(t) {
        return [CommunicationRoleEnum.FIRM, CommunicationRoleEnum.BS].map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createCompanyOptions = createCompanyOptions;
    function createClubOptions(t) {
        return [CommunicationRoleEnum.FIRM, CommunicationRoleEnum.BS].map((communicationRole) => ({
            label: PartyCommunicationRole.getTranslatedValue(communicationRole, t),
            value: communicationRole,
        }));
    }
    PartyCommunicationRole.createClubOptions = createClubOptions;
    function getTranslatedValue(value, t) {
        return t(`party:communicationRole.${value}`);
    }
    PartyCommunicationRole.getTranslatedValue = getTranslatedValue;
})(PartyCommunicationRole || (PartyCommunicationRole = {}));
export var PartyLanguageIsoCode;
(function (PartyLanguageIsoCode) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(LanguageIsoCodeEnum);
    }
    PartyLanguageIsoCode.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyLanguageIsoCode.getAllValues().map((language) => ({
            label: PartyLanguageIsoCode.getTranslatedValue(language, t),
            value: language,
        }));
    }
    PartyLanguageIsoCode.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:languages.${value}`);
    }
    PartyLanguageIsoCode.getTranslatedValue = getTranslatedValue;
})(PartyLanguageIsoCode || (PartyLanguageIsoCode = {}));
export var PartyCountryCallingCode;
(function (PartyCountryCallingCode) {
    const prioritizedCountries = [
        CountryCallingCodeEnum.C43,
        CountryCallingCodeEnum.C49,
        CountryCallingCodeEnum.C41,
    ];
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(CountryCallingCodeEnum);
    }
    PartyCountryCallingCode.getAllValues = getAllValues;
    function createOptions() {
        return PartyCountryCallingCode.getAllValues().map((countryCallingCode) => ({
            label: countryCallingCode,
            value: countryCallingCode,
        }));
    }
    PartyCountryCallingCode.createOptions = createOptions;
    function createSortedOptions() {
        const countryCallingCodes = PartyCountryCallingCode.getAllValues();
        const filteredCountries = countryCallingCodes.filter((countryCallingCode) => !prioritizedCountries.includes(countryCallingCode));
        const sortedCountries = [...prioritizedCountries, ...filteredCountries];
        return sortedCountries.map((countryCallingCode) => ({
            label: countryCallingCode,
            value: countryCallingCode,
        }));
    }
    PartyCountryCallingCode.createSortedOptions = createSortedOptions;
    function createHighlightedOptions() {
        return prioritizedCountries.map((countryCallingCode) => ({
            label: countryCallingCode,
            value: countryCallingCode,
        }));
    }
    PartyCountryCallingCode.createHighlightedOptions = createHighlightedOptions;
    function getTranslatedValue(value, t) {
        return t(`party:countries.${value}`);
    }
    PartyCountryCallingCode.getTranslatedValue = getTranslatedValue;
})(PartyCountryCallingCode || (PartyCountryCallingCode = {}));
export var PartyCountryIsoCode;
(function (PartyCountryIsoCode) {
    const prioritizedCountries = [
        CountryIsoCodeEnum.AT,
        CountryIsoCodeEnum.DE,
        CountryIsoCodeEnum.CH,
    ];
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(CountryIsoCodeEnum) || true;
    }
    PartyCountryIsoCode.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyCountryIsoCode.getAllValues().map((country) => ({
            label: PartyCountryIsoCode.getTranslatedValue(country, t),
            value: country,
        }));
    }
    PartyCountryIsoCode.createOptions = createOptions;
    function createSortedOptions(t) {
        const countries = PartyCountryIsoCode.getAllValues();
        const filteredCountries = countries.filter((country) => !prioritizedCountries.includes(country));
        const sortedCountries = [...prioritizedCountries, ...filteredCountries];
        return sortedCountries.map((country) => ({
            label: PartyCountryIsoCode.getTranslatedValue(country, t),
            value: country,
        }));
    }
    PartyCountryIsoCode.createSortedOptions = createSortedOptions;
    function createHighlightedOptions(t) {
        return prioritizedCountries.map((country) => ({
            label: PartyCountryIsoCode.getTranslatedValue(country, t),
            value: country,
        }));
    }
    PartyCountryIsoCode.createHighlightedOptions = createHighlightedOptions;
    // if the translation can not be found, the enum value itself will be returned as string
    function getTranslatedValue(value, t) {
        return value
            ? t(`party:countries.${value}`, { defaultValue: value })
            : '';
    }
    PartyCountryIsoCode.getTranslatedValue = getTranslatedValue;
})(PartyCountryIsoCode || (PartyCountryIsoCode = {}));
export var PartyEmployment;
(function (PartyEmployment) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(EmploymentEnum);
    }
    PartyEmployment.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyEmployment.getAllValues().map((employment) => ({
            label: PartyEmployment.getTranslatedValue(employment, t),
            value: employment,
        }));
    }
    PartyEmployment.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:employment.${value}`);
    }
    PartyEmployment.getTranslatedValue = getTranslatedValue;
})(PartyEmployment || (PartyEmployment = {}));
export var PartyEducation;
(function (PartyEducation) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(EducationEnum);
    }
    PartyEducation.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyEducation.getAllValues().map((education) => ({
            label: PartyEducation.getTranslatedValue(education, t),
            value: education,
        }));
    }
    PartyEducation.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:education.${value}`);
    }
    PartyEducation.getTranslatedValue = getTranslatedValue;
})(PartyEducation || (PartyEducation = {}));
export var PartyOccupation;
(function (PartyOccupation) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(OccupationEnum);
    }
    PartyOccupation.getAllValues = getAllValues;
    function createOptions(salutation, t) {
        return PartyOccupation.getAllValues().map((occupation) => ({
            label: PartyOccupation.getTranslatedValue(occupation, salutation, t),
            value: occupation,
        }));
    }
    PartyOccupation.createOptions = createOptions;
    function getTranslatedValue(value, salutation, t) {
        return t(`party:occupation.${value}.${PartySalutation.inferGender(salutation)}`);
    }
    PartyOccupation.getTranslatedValue = getTranslatedValue;
})(PartyOccupation || (PartyOccupation = {}));
export var PartyOrganizationType;
(function (PartyOrganizationType) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(OrganizationTypeEnum);
    }
    PartyOrganizationType.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyOrganizationType.getAllValues().map((country) => ({
            label: PartyOrganizationType.getTranslatedValue(country, t),
            value: country,
        }));
    }
    PartyOrganizationType.createOptions = createOptions;
    function createCompanyOptions(t) {
        return PartyOrganizationType.getAllValues()
            .filter((type) => ['F', 'I', 'O', 'V', 'K'].includes(type))
            .map((country) => ({
            label: PartyOrganizationType.getTranslatedValue(country, t),
            value: country,
        }));
    }
    PartyOrganizationType.createCompanyOptions = createCompanyOptions;
    function createEinzelUnternehmerOptions(t) {
        return PartyOrganizationType.getAllValues()
            .filter((type) => ['L', 'B'].includes(type))
            .map((organizationType) => ({
            label: PartyOrganizationType.getTranslatedValue(organizationType, t),
            value: organizationType,
        }));
    }
    PartyOrganizationType.createEinzelUnternehmerOptions = createEinzelUnternehmerOptions;
    function createClubOptions(t) {
        return PartyOrganizationType.getAllValues()
            .filter((type) => ['F', 'I', 'O', 'V'].includes(type))
            .map((country) => ({
            label: PartyOrganizationType.getTranslatedValue(country, t),
            value: country,
        }));
    }
    PartyOrganizationType.createClubOptions = createClubOptions;
    function getTranslatedValue(value, t) {
        return t(`party:organizationType.${value}`);
    }
    PartyOrganizationType.getTranslatedValue = getTranslatedValue;
})(PartyOrganizationType || (PartyOrganizationType = {}));
export var PartyCivilStatus;
(function (PartyCivilStatus) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(CivilStatusEnum);
    }
    PartyCivilStatus.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyCivilStatus.getAllValues().map((country) => ({
            label: PartyCivilStatus.getTranslatedValue(country, t),
            value: country,
        }));
    }
    PartyCivilStatus.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:civilStatus.${value}`);
    }
    PartyCivilStatus.getTranslatedValue = getTranslatedValue;
})(PartyCivilStatus || (PartyCivilStatus = {}));
export var PartyLegalForm;
(function (PartyLegalForm) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(LegalFormEnum);
    }
    PartyLegalForm.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyLegalForm.getAllValues().map((legalForm) => ({
            label: PartyLegalForm.getTranslatedValue(legalForm, t),
            value: legalForm,
        }));
    }
    PartyLegalForm.createOptions = createOptions;
    function createSubTypeOptions(partyType, t) {
        const legalFormValues = PartyLegalForm.getAllValues();
        switch (partyType) {
            case PartySubTypeEnum.EINZELUNTERNEHMER:
                return legalFormValues
                    .filter((type) => [LegalFormEnum.UNB, LegalFormEnum.EINZL].includes(type))
                    .map((legalForm) => ({
                    label: PartyLegalForm.getTranslatedValue(legalForm, t),
                    value: legalForm,
                }));
            case PartySubTypeEnum.VEREIN:
                return legalFormValues
                    .filter((type) => [LegalFormEnum.UNB, LegalFormEnum.VEREIN].includes(type))
                    .map((legalForm) => ({
                    label: PartyLegalForm.getTranslatedValue(legalForm, t),
                    value: legalForm,
                }));
            case PartySubTypeEnum.UNTERNEHMEN:
                return legalFormValues
                    .filter((type) => ![LegalFormEnum.EINZL, LegalFormEnum.VEREIN].includes(type))
                    .map((legalForm) => ({
                    label: PartyLegalForm.getTranslatedValue(legalForm, t),
                    value: legalForm,
                }));
            default:
                return [];
        }
    }
    PartyLegalForm.createSubTypeOptions = createSubTypeOptions;
    function getTranslatedValue(value, t) {
        return t(`party:legalForm.${value}`);
    }
    PartyLegalForm.getTranslatedValue = getTranslatedValue;
})(PartyLegalForm || (PartyLegalForm = {}));
export var FixedParty;
(function (FixedParty) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(FixedPartyEnum);
    }
    FixedParty.getAllValues = getAllValues;
    function createOptions(t) {
        return FixedParty.getAllValues().map((fixedParty) => ({
            label: FixedParty.getTranslatedValue(fixedParty, t),
            value: fixedParty,
        }));
    }
    FixedParty.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:fixedParty.${value}`);
    }
    FixedParty.getTranslatedValue = getTranslatedValue;
})(FixedParty || (FixedParty = {}));
export var PartyDiplomaticStatus;
(function (PartyDiplomaticStatus) {
    function getAllValues() {
        return enumWithNumberValuesToArrayOfKeys(DiplomaticStatusEnum);
    }
    PartyDiplomaticStatus.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyDiplomaticStatus.getAllValues().map((diplomaticStatus) => ({
            label: PartyDiplomaticStatus.getTranslatedValue(diplomaticStatus, t),
            value: diplomaticStatus,
        }));
    }
    PartyDiplomaticStatus.createOptions = createOptions;
    function createOptionsPerson(t) {
        return PartyDiplomaticStatus.getAllValues()
            .filter((d) => d !== DiplomaticStatusEnum.IO)
            .map((diplomaticStatus) => ({
            label: PartyDiplomaticStatus.getTranslatedValue(diplomaticStatus, t),
            value: diplomaticStatus,
        }));
    }
    PartyDiplomaticStatus.createOptionsPerson = createOptionsPerson;
    function createOptionsLegalEntity(t) {
        return [
            {
                label: PartyDiplomaticStatus.getTranslatedValue(DiplomaticStatusEnum.IO, t),
                value: DiplomaticStatusEnum.IO,
            },
        ];
    }
    PartyDiplomaticStatus.createOptionsLegalEntity = createOptionsLegalEntity;
    function getTranslatedValue(value, t) {
        return t(`party:diplomaticStatus.${value}`);
    }
    PartyDiplomaticStatus.getTranslatedValue = getTranslatedValue;
})(PartyDiplomaticStatus || (PartyDiplomaticStatus = {}));
export var PartyInsolvencyProceeding;
(function (PartyInsolvencyProceeding) {
    function getAllValues() {
        return enumWithStringValuesToArrayOfKeys(InsolvencyEnum);
    }
    PartyInsolvencyProceeding.getAllValues = getAllValues;
    function createOptions(t) {
        return PartyInsolvencyProceeding.getAllValues().map((legalForm) => ({
            label: PartyInsolvencyProceeding.getTranslatedValue(legalForm, t),
            value: legalForm,
        }));
    }
    PartyInsolvencyProceeding.createOptions = createOptions;
    function getTranslatedValue(value, t) {
        return t(`party:insolvencyProceeding.${value}`);
    }
    PartyInsolvencyProceeding.getTranslatedValue = getTranslatedValue;
})(PartyInsolvencyProceeding || (PartyInsolvencyProceeding = {}));
export var PartyBankAccount;
(function (PartyBankAccount) {
    function createDefault() {
        return {
            name: null,
            iban: null,
            holder: null,
            bic: null,
        };
    }
    PartyBankAccount.createDefault = createDefault;
})(PartyBankAccount || (PartyBankAccount = {}));
